import React, { useState, useEffect, useCallback } from "react"
import IntlProps from "../../../state/helpers/IntlProps"
import { FormattedMessage, FormattedNumber, injectIntl } from "react-intl"
import { ProductPriceListingService } from "../../../state/listing/product-price/service"
import { ProductPriceListingProps, ProductPriceWithProductInfo } from "../../../common/types/productPrice/types"
import { ProductPriceDetails } from "../../../state/product-price/types"
import { PRODUCT_PRICE_UPDATE_STATUS } from "../../../utils/product-price"
import StandardList from "../../../common/listing/list.component.jsx"
import { PaginatedSearchPayload } from "../../../state/search/SearchPayload"
import Pagination from "../../../common/listing/pagination.component"
import LoadingSpinner from "../../../common/spinner/loading.component"
import useSaveChangesBeforeNavigateModal from "../../../common/hooks/UseSaveChangesBeforeNavigateModal"
import MoneyInputForm from "../../../common/form/moneyinput.component"
import Icon from "../../../common/icon/icon.component"
import UpdateStatusBadge from "../../../common/badge/UpdateStatusBadge"
import "../styles/ProductPriceListingStyles.css"
import {
    getMargin,
    getMarginalUnitPrice
} from "../../../utils/margin/utils"
import { Button } from "react-bootstrap"


const ProductPriceListing = (props: ProductPriceListingProps & IntlProps) => {
    const [searchFilters, setSearchFilters] = useState<PaginatedSearchPayload>({
        keywords: "",
        activePage: 1,
        sortByField: "price",
        pageSize: 10,
        sortAsc: false
    })
    const [loading, setLoading] = useState<boolean>(true)
    const {modal, toggleShow, hasConfirmed} = useSaveChangesBeforeNavigateModal()
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0)
    const [itemsUpdatedCount, setItemsUpdatedCount] = useState<number>(0)
    const [newPage, setNewPage] = useState<number>(1)

    const createProductPricesWithProductInfo = (items: ProductPriceDetails[]): ProductPriceWithProductInfo[] => {
        return items.map(item => {
            return {
                ...item,
                updateStatus: "",
                recentlyModified: false,
                profitMargin: getMargin(item.defaultUnitCost, item.price),
                toBeArchived: false,
            }
        })
    }

    const fetchListing = useCallback (async () => {
        setLoading(true)

        const res = await ProductPriceListingService.getProductPricesWithProductInfo({
            ...searchFilters,
            priceLevelId: props.priceLevelId,
        })
        if (res && res.data.data.length > 0) {
            setTotalItemsCount(res.data.metadata.totalItemsCount)
            props.onListingChangeCallback(createProductPricesWithProductInfo(res.getListing()))
        }

        setLoading(false)
    }, [searchFilters.activePage, searchFilters.pageSize])

    useEffect(() => {
        fetchListing()
    }, [fetchListing])

    useEffect(() => {
        if(hasConfirmed) {
            props.onUpdateCallback()
            setSearchFilters({
                ...searchFilters,
                activePage: newPage
            })
            
        }
    }, [hasConfirmed])

    const headers = {
        classNames: [
            "standard-big-column",
            "standard-list-small-column",
            "standard-list-small-column",
            "standard-big-column"],
        data: [
            <FormattedMessage
                key="product.product_price.product_price_listing_product"
                id="product.product_price.product_price_listing_product"
                defaultMessage="Product"
            />,
            <FormattedMessage
                key="product.product_price.product_price_listing_cost"
                id="product.product_price.product_price_listing_cost"
                defaultMessage="Cost"
            />,
            <FormattedMessage
                key="product.product_price.product_price_listing_margin"
                id="product.product_price.product_price_listing_margin"
                defaultMessage="Margin"
            />,
            <FormattedMessage
                key="product.product_price.product_price_listing_price"
                id="product.product_price.product_price_listing_price"
                defaultMessage="Price"
            />,
        ]
    }

    const getPriceColumn = (productPrice: ProductPriceWithProductInfo) => {
        return (<div className={"d-flex justify-content-between flex-wrap"}>
            <div className="flex-basis-50 qm-pl-0">
                <MoneyInputForm
                    currency={productPrice.currency}
                    name="productPricePriceUnitPrice"
                    id={productPrice.productId}
                    inputClassName={"text-right"}
                    key={productPrice.id}
                    value={productPrice.price}
                    debounced={true}
                    onChange={val => handleDefaultUnitPriceChange(productPrice.productId, val)}
                />
            </div>
            <div className="text-align-right align-self-center" id={"labelContainer" + productPrice.productId}>
                <UpdateStatusBadge status={productPrice.updateStatus} />
            </div>
            <div className="text-align-right align-self-center">
                <Button 
                    onClick={() => handleDeleteProduct(productPrice.productId)}
                    id={productPrice.productId}
                    variant="danger"
                    size="sm"
                >
                    <Icon value={"trash"} className={"mr-1"} />
                </Button>
            </div>
        </div>)
    }

    const handleMarginRatioChange = (event) => {
        const items: ProductPriceWithProductInfo[] = props.productPricesWithProductInfo.map(item =>
            item.productId === event.target.id
                ? {
                    ...item,
                    profitMargin: event.target.value,
                    price: getMarginalUnitPrice(item.defaultUnitCost, event.target.value),
                    updateStatus: PRODUCT_PRICE_UPDATE_STATUS.MODIFIED,
                    recentlyModified: true,
                    toBeArchived: false
                }
                : item
        )
        props.onListingChangeCallback(items)
        setItemsUpdatedCount(itemsUpdatedCount + 1)
    }

    const handleDefaultUnitPriceChange = (productId: string, value) => {
        const items: ProductPriceWithProductInfo[] = props.productPricesWithProductInfo.map(item =>
            item.productId === productId
                ? {
                    ...item,
                    profitMargin: getMargin(item.defaultUnitCost, value),
                    price: value,
                    updateStatus: PRODUCT_PRICE_UPDATE_STATUS.MODIFIED,
                    recentlyModified: true,
                    toBeArchived: false
                }
                : item
        )
        props.onListingChangeCallback(items)
        setItemsUpdatedCount(itemsUpdatedCount + 1)
    }

    const handleDeleteProduct = (productId: string) => {
        const items: ProductPriceWithProductInfo[] = props.productPricesWithProductInfo.map((item) =>
            item.productId === productId
                ? {
                    ...item,
                    updateStatus: PRODUCT_PRICE_UPDATE_STATUS.DELETED,
                    recentlyModified: true,
                    toBeArchived: true
                }
                : item
        )
        props.onListingChangeCallback(items)
        setItemsUpdatedCount(itemsUpdatedCount + 1)
    }

    const rows = props.productPricesWithProductInfo.map(item => {
        const name = [] as JSX.Element[]
        name.push(<p key={item.id} className={"m-0"}>{item.name}</p>)

        const defaultUnitCost = [] as JSX.Element[]
        defaultUnitCost.push(<FormattedNumber
            style="currency"
            currency={item.currency}
            value={item.defaultUnitCost}
        />)

        const profitMargin = [] as JSX.Element[]
        profitMargin.push(<input
            name="marginRatio"
            value={item.profitMargin}
            onChange={handleMarginRatioChange}
            id={item.productId}
            key={item.id}
            type="number"
            className={"form-control " + (item.profitMargin > 0 ? "text-success" : "")}
        />)

        return {
            classNames: [
                "standard-big-column",
                "standard-list-small-column",
                "standard-list-small-column",
                "standard-big-column"],
            data: [
                name,
                defaultUnitCost,
                profitMargin,
                getPriceColumn(item)
            ]
        }
    })

    const handlePageSizeChange = (size: number) => {
        setSearchFilters({
            ...searchFilters,
            activePage: 1,
            pageSize: size
        })
    }

    const hasRecentlyModified = () => {
        const items = props.productPricesWithProductInfo
        for (const prod of items) {
            if (prod.recentlyModified === true) {
                return true
            }
        }
        return false
    }

    const handlePageChange = (pageNumber: number) => {
        if (!hasRecentlyModified()) {
            setSearchFilters({
                ...searchFilters,
                activePage: pageNumber
            })
        } else {
            setNewPage(pageNumber)
            toggleShow()
        }
    }

    const listing = <>
        <StandardList
            smallLine={true}
            className="row"
            headers={headers}
            rows={rows}
        />
        <div className="pb-2">
            <Pagination
                key={itemsUpdatedCount}
                showPageSize={true}
                onPageSizeChange={handlePageSizeChange}
                activePage={searchFilters.activePage}
                pageSize={searchFilters.pageSize}
                totalItemsCount={totalItemsCount}
                onChange={handlePageChange}
            >
            </Pagination>
        </div>
    </>

    if (loading) {
        return <LoadingSpinner className="pt-4" />
    }

    return (<>
        {modal}
        {listing}
    </>
    )
}

export default injectIntl(ProductPriceListing)


// WEBPACK FOOTER //
// ./src/product/product-price/components/ProductPriceListing.tsx